import React, { useState, useEffect } from 'react';
import { Col, Divider, notification, Row, Typography, Spin } from 'antd';
import { ApiCard } from 'components/api-card/ApiCard';
import { useGlobalApiLimits, useSaveGlobalApiLimits } from 'api/site_administration';
import { ChangedEndpointPayload } from 'types/SiteAdministration';

type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

interface Endpoint {
  endpointId: number;
  method: HttpMethod;
  path: string;
  limit: number;
  unit: string;
}

interface ApiGroup {
  id: string;
  title: string;
  endpoints: Endpoint[];
}

const durationToUnit = (seconds: number): string => {
  if (seconds === 86400) return 'Day';
  if (seconds === 3600) return 'Hour';
  if (seconds === 60) return 'Minute';
  return 'Second';
};

const unitToDuration = (unit: string): number => {
  if (unit === 'Day') return 86400;
  if (unit === 'Hour') return 3600;
  if (unit === 'Minute') return 60;
  return 1;
};

const GlobalApiLimits: React.FC = () => {
  const { data: globalRateLimits, isLoading } = useGlobalApiLimits();
  const { mutateAsync: saveGlobalLimits } = useSaveGlobalApiLimits();

  const [apiGroups, setApiGroups] = useState<ApiGroup[]>([]);
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [savingGroupId, setSavingGroupId] = useState<string | null>(null);

  useEffect(() => {
    if (globalRateLimits && globalRateLimits.integrations) {
      const groups: ApiGroup[] = globalRateLimits.integrations.map((integration) => ({
        id: integration.moduleName,
        title: integration.moduleName,
        endpoints: integration.endpoints.map((ep) => ({
          endpointId: ep.endpointId,
          method: ep.httpMethod as HttpMethod,
          path: ep.pathTemplate,
          limit: ep.effectiveGlobal.points,
          unit: durationToUnit(ep.effectiveGlobal.durationSeconds)
        }))
      }));
      setApiGroups(groups);
    }
  }, [globalRateLimits]);

  const handleUpdateEndpoint = (groupId: string, index: number, patch: Partial<Endpoint>) => {
    setApiGroups((prev) =>
      prev.map((g) =>
        g.id === groupId
          ? {
              ...g,
              endpoints: g.endpoints.map((ep, i) => (i === index ? { ...ep, ...patch } : ep))
            }
          : g
      )
    );
  };

  const handleSave = async (groupId: string, endpoints: Endpoint[]) => {
    if (!globalRateLimits) return;

    try {
      const originalIntegration = globalRateLimits.integrations.find((i) => i.moduleName === groupId);

      const changedEndpoints: ChangedEndpointPayload[] = [];

      endpoints.forEach((ep) => {
        const originalEp = originalIntegration?.endpoints.find((oep) => oep.endpointId === ep.endpointId);

        if (originalEp) {
          const originalLimit = originalEp.effectiveGlobal.points;
          const originalUnit = durationToUnit(originalEp.effectiveGlobal.durationSeconds);

          if (ep.limit !== originalLimit || ep.unit !== originalUnit) {
            changedEndpoints.push({
              endpointId: ep.endpointId,
              desiredGlobalOverride: {
                points: ep.limit,
                durationSeconds: unitToDuration(ep.unit)
              }
            });
          }
        }
      });

      if (changedEndpoints.length > 0) {
        setSavingGroupId(groupId);
        await saveGlobalLimits({
          globalRevisionToken: globalRateLimits.globalRevisionToken,
          changedEndpoints
        });
        notification.success({
          message: `${groupId} updated successfully`
        });
      } else {
        // Handle case where they clicked save but didn't change anything
        setEditingGroupId(null);
        return;
      }

      setEditingGroupId(null);
    } catch (error: any) {
      notification.error({
        message: `Failed to update ${groupId}`,
        description: error?.message || 'An unexpected error occurred.'
      });
    } finally {
      setSavingGroupId((prev) => (prev === groupId ? null : prev));
    }
  };

  return (
    <div className="p-8">
      <Typography.Title level={3}>Global Settings</Typography.Title>

      <Divider className="my-4" />

      <div className="mt-6">
        <Typography.Title level={4} className="!mb-1">
          API Configurations
        </Typography.Title>
        <Typography.Paragraph className="text-black">
          Manage global API rate limits across all clients
        </Typography.Paragraph>

        {isLoading ? (
          <div className="flex justify-center p-12">
            <Spin size="large" />
          </div>
        ) : (
          <Row gutter={[24, 24]}>
            {apiGroups.map((group) => {
              const isEditing = editingGroupId === group.id;
              const isSaving = savingGroupId === group.id;

              return (
                <Col xs={24} lg={12} key={group.id}>
                  <ApiCard
                    title={group.title}
                    endpoints={group.endpoints as any}
                    // 🚫 No exceptions here
                    showExceptions={false}
                    isEditing={isEditing}
                    isSaving={isSaving}
                    onStartEdit={() => setEditingGroupId(group.id)}
                    onCancelEdit={() => {
                      setEditingGroupId(null);
                      // Re-trigger useEffect to reset state
                      if (globalRateLimits && globalRateLimits.integrations) {
                        const integration = globalRateLimits.integrations.find((i) => i.moduleName === group.id);
                        if (integration) {
                          setApiGroups((prev) =>
                            prev.map((g) =>
                              g.id === group.id
                                ? {
                                    ...g,
                                    endpoints: integration.endpoints.map((ep) => ({
                                      endpointId: ep.endpointId,
                                      method: ep.httpMethod as HttpMethod,
                                      path: ep.pathTemplate,
                                      limit: ep.effectiveGlobal.points,
                                      unit: durationToUnit(ep.effectiveGlobal.durationSeconds)
                                    }))
                                  }
                                : g
                            )
                          );
                        }
                      }
                    }}
                    onSave={() => handleSave(group.id, group.endpoints)}
                    onUpdateEndpoint={(index: number, patch: any) => handleUpdateEndpoint(group.id, index, patch)}
                  />
                </Col>
              );
            })}
          </Row>
        )}
      </div>
    </div>
  );
};

export default GlobalApiLimits;
